import jsPDF from "jspdf";
import { logoBase64 } from "../../../public/assets/logoBase64";
import { ComisionesDetallesResponse } from "../paginas/comisiones/components/detalles/detalles.component";
import { ComisionesDetallesPorCobroResponse } from "../paginas/comisiones/components/detalles-por-cobro/detalles-por-cobro.component";

/**
 * Genera y descarga un PDF a partir de un elemento HTML visible en pantalla.
 *
 * @param element - Elemento HTML que será convertido a PDF.
 * @param title - Título usado como nombre del archivo descargado.
 */
export const exportarVolantePDF = (element: HTMLElement, title: string): void => {
  element.classList.toggle('show');

  // Crear instancia de jsPDF con orientación horizontal (landscape)
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  // Generar el PDF a partir del contenido HTML
  doc.html(element, {
    callback: (doc) => {
      doc.save(`${title}.pdf`); // Descargar el archivo PDF
    },
    x: 10,
    y: 10,
    html2canvas: {
      scale: 0.22, // Escala del renderizado del HTML
    },
  });

  // Ocultar el contenido después de la generación del PDF
  setTimeout(() => {
    element.classList.toggle('show')
  }, 50);
};

/**
 * Formatea un número a dos decimales con separador de miles.
 *
 * @param value - Número a formatear.
 * @returns Número como string con formato de moneda.
 */
const formatNumber = (value: number): string =>
  new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

/**
 * Formatea una fecha al formato dd/mm/yyyy.
 *
 * @param date - Fecha a formatear.
 * @returns Fecha como string legible en formato español.
 */
const formatDate = (date: Date): string =>
  new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);


interface paramsDetalles {
  data: Record<string, Record<string, { records: ComisionesDetallesResponse[], premiumNetaTotal: number, commissionTotal: number, }>>
  usuario: string | null;
  fechaCorte: Date | undefined;
  tipoCorte: string;
  productor: string;
  currencyTotalCommission: Record<string, { commissionSCoaseguroTotal: number, commissionCoaseguroTotal: number }>;
  currencyTotalPremiumNeta: Record<string, { premiumNetaSCoaseguroTotal: number, premiumNetaCoaseguroTotal: number }>;
}
/**
 * Exporta en formato PDF los detalles de comisiones agrupadas por moneda y subramo.
 * El reporte incluye montos, tasas, primas netas y totales generales.
 *
 * @param params - Objeto con los datos necesarios para construir el reporte.
 * @param params.data - Datos de comisiones agrupados por moneda y subramo.
 * @param params.usuario - Nombre del usuario que genera el reporte.
 * @param params.fechaCorte - Fecha del período de corte del reporte.
 * @param params.tipoCorte - Tipo de corte (mensual, anual, etc).
 * @param params.productor - Nombre del productor relacionado al reporte.
 * @param params.currencyTotalCommission - Totales de comisiones por moneda.
 * @param params.currencyTotalPremiumNeta - Totales de primas netas por moneda.
 */
export const exportarDetallesPDF = ({
  data,
  usuario,
  fechaCorte,
  tipoCorte,
  productor,
  currencyTotalCommission,
  currencyTotalPremiumNeta,
}: paramsDetalles): void => {
  const doc = new jsPDF({ orientation: "landscape" }) as any;
  const fechaFormat = fechaCorte?.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
  let currentY = 15;
  const pageHeight = doc.internal.pageSize.height;

  /**
   * Renderiza el encabezado del documento con información general del reporte.
   */
  const drawHeader = () => {
    doc.addImage(logoBase64, "PNG", 10, 10, 40, 13);
    doc.setFontSize(18);
    doc.text(`Detalles comisiones - Portal Intermediario`, 55, 20);
    doc.setFontSize(12);
    doc.text(`Período: ${fechaFormat}`, 15, 30);
    doc.text(`${tipoCorte}`, 15, 35);
    doc.text(`Productor: ${productor}`, 15, 40);
    currentY += 11;
  };

  // Itera por cada moneda
  Object.keys(data).forEach((currency, currencyIdx) => {
    if (currencyIdx > 0) {
      doc.addPage();
      doc.autoTable({ didDrawPage: drawHeader });
      currentY = 15;
    } else {
      doc.autoTable({ didDrawPage: () => drawHeader() });
    }

    const subramos = Object.keys(data[currency]);

    // Itera por cada subramo dentro de la moneda
    subramos.forEach((subramo, subramoIdx) => {
      if (currentY + 75 > pageHeight) {
        doc.addPage();
        currentY = 0;
      }

      const body: any[] = [];

      // Encabezado de tabla
      body.push([
        'Deudor', 'Póliza', 'Factura', 'Documento', 'Emisión',
        'Tasa', '% Coaseguro', 'Cobertura', 'Prima Bruta', 'Prima Neta',
        '% Comisión', 'Monto comisión',
      ]);

      // Línea con el nombre de la moneda
      body.push([currency]);

      // Registros por subramo
      const records = data[currency][subramo].records.map(record => [
        record.clientName,
        record.policyNumber,
        record.invoiceNumber,
        record.document,
        formatDate(new Date(record.wDate)),
        formatNumber(record.rCurrency),
        formatNumber(record.coinsurancePercent),
        record.coverageGroupDescription,
        formatNumber(record.grossPremium),
        formatNumber(record.netPremium),
        formatNumber(record.commissionRate),
        formatNumber(record.commission),
      ]);

      body.push(...records);

      // Fila con total del subramo
      body.push([
        '', '', '', '', '', '', '', `Total ${subramo}`,'',
        formatNumber(data[currency][subramo].premiumNetaTotal),
        '',
        formatNumber(data[currency][subramo].commissionTotal),
      ]);

      // Si es el último subramo, se agregan totales por moneda
      if (subramoIdx === subramos.length - 1) {
        const netaS = currencyTotalPremiumNeta[currency].premiumNetaSCoaseguroTotal;
        const netaC = currencyTotalPremiumNeta[currency].premiumNetaCoaseguroTotal;
        const comS = currencyTotalCommission[currency].commissionSCoaseguroTotal;
        const comC = currencyTotalCommission[currency].commissionCoaseguroTotal;

        body.push(
          ['', '', '', '', '', '', '', '', '', '', ''],
          ['', '', '', '', '', '', 'Total', '', '', '', ''],
          ['', '', '', '', '', '', '', `Comisión ${currency}`, formatNumber(netaS), '', formatNumber(comS)],
          ['', '', '', '', '', '', '', `Coaseguro ${currency}`, formatNumber(netaC), '', formatNumber(comC)],
          ['', '', '', '', '', '', '', '', formatNumber(netaS + netaC), '', formatNumber(comS + comC)],
        );
      }

      // Renderiza tabla en el PDF
      doc.autoTable({
        startY: currentY + 15,
        body,
        margin: { left: 10 },
        styles: {
          fontSize: 9,
          rowHeight: 5,
        },
        columnStyles: {
          0: { halign: 'left' },
          1: { halign: 'left' },
          2: { halign: 'right' },
          3: { halign: 'left' },
          4: { halign: 'right' },
          5: { halign: 'right' },
          6: { halign: 'right' },
          7: { halign: 'left' },
          8: { halign: 'right' },
          9: { halign: 'right' },
          10: { halign: 'right' },
        },
        /**
         * Aplica estilos personalizados a ciertas filas del cuerpo de la tabla.
         */
        didParseCell: function (row: any) {
          const isBody = row.section === 'body';
          const lastIndex = row.table.body.length - 1;
          const currentIndex = row.row.index;

          // Estilo de encabezado de columnas
          if (currentIndex === 0 && isBody) {
            Object.assign(row.cell.styles, {
              valign: 'bottom',
              lineWidth: { bottom: 1 },
              lineColor: [255, 165, 0],
              fillColor: [255, 255, 255],
              textColor: [0, 0, 0],
              fontStyle: 'bold',
              fontSize: 10,
            });
          }

          // Estilo del nombre de la moneda
          if (currentIndex === 1 && isBody) {
            row.cell.styles.fontStyle = 'bold';
            row.cell.styles.textDecoration = 'underline';
          }

          // Si estamos en el último subramo...
          const isLastSubramo = subramoIdx === subramos.length - 1;
          if (isLastSubramo) {
            if ([lastIndex - 6, lastIndex - 4, lastIndex - 3, lastIndex - 2, lastIndex].includes(currentIndex)) {
              Object.assign(row.cell.styles, {
                fillColor: [255, 255, 255],
                textColor: [0, 0, 0],
                fontStyle: 'bold',
                fontSize: currentIndex === lastIndex - 4 ? 10 : 9,
                lineWidth: [lastIndex - 4, lastIndex - 2].includes(currentIndex) ? { bottom: 1 } : 0,
                lineColor: currentIndex === lastIndex - 2 ? [52, 201, 235] : [0, 0, 0],
              });
            }
          } else {
            if (currentIndex === lastIndex && isBody) {
              Object.assign(row.cell.styles, {
                fillColor: [255, 255, 255],
                textColor: [0, 0, 0],
                fontStyle: 'bold',
                fontSize: 10,
              });
            }
          }
        },
      });

      currentY = doc.lastAutoTable.finalY - 10;
    });
  });

  // Descarga el archivo generado
  doc.save("Comisiones Detalles.pdf");
};


interface paramsDetallerPorCobro {
  data: Record<string, { records: ComisionesDetallesPorCobroResponse[], premiumNetaTotal: number, commissionTotal: number, participationTotal: number }>;
  primaNetaTotal: number;
  participacionTotal: number;
  comisionPrimaNetaTotal: number;
  usuario: string | null;
  fechaCorte: Date | undefined;
  productor: string;
}

/**
 * Genera un PDF con los detalles de comisiones por cobro agrupados por subramo.
 */
export const exportarDetallesPorCobroPDF = ({
  data,
  usuario,
  fechaCorte,
  productor,
  primaNetaTotal,
  participacionTotal,
  comisionPrimaNetaTotal
}: paramsDetallerPorCobro) => {
  const doc = new jsPDF({ orientation: "landscape" }) as any;
  let currentY = 10;
  const pageHeight = doc.internal.pageSize.height;
  const fechaFormat = fechaCorte?.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });

  /**
   * Dibuja el encabezado de la página.
   */
  const drawHeader = () => {
    doc.addImage(logoBase64, "PNG", 10, 10, 40, 13);
    doc.setFontSize(12);
    doc.text(`Detalles comisiones por cobro - Portal Intermediario`, 55, 15);
    doc.text(`${usuario}`, 55, 20);
    doc.text(`Período: ${fechaFormat}`, 15, 30);
    doc.text(`Corte mensual`, 15, 35);
    doc.text(`Productor: ${productor}`, 15, 40);
    currentY += 11;
  };

  doc.autoTable({ didDrawPage: () => drawHeader() });

  const subramos = Object.keys(data);

  subramos.forEach((subramo, idx) => {
    if (currentY + 75 > pageHeight) {
      doc.addPage();
      currentY = 0;
    }

    const body: any[] = [];

    // Encabezado
    body.push([
      'Deudor', 'Póliza', 'Factura', 'Documento', 'Emisión',
      'Cobertura', 'Prima Bruta','Prima Neta', 'Participación Intermediario',
      '% Comisión', 'Monto Comisión'
    ]);

    // Registros
    body.push(...data[subramo].records.map(record => [
      record.clientName,
      record.policyNumber,
      record.invoiceNumber,
      record.document,
      formatDate(new Date(record.wDate)),
      record.gCovDescription,
      formatNumber(record.grossPremium),
      formatNumber(record.coverageValue),
      formatNumber(record.coverageValuePart),
      formatNumber(record.commissionPercentage),
      formatNumber(record.comision),
    ]));

    // Totales por subramo
    body.push([
      '', '', '', '', '',
      `Total ${subramo}`,'',
      formatNumber(data[subramo].premiumNetaTotal),
      formatNumber(data[subramo].participationTotal),
      '',
      formatNumber(data[subramo].commissionTotal),
    ]);

    // Totales finales (solo en el último subramo)
    if (idx === subramos.length - 1) {
      body.push(
        ['', '', '', '', '', '', '', '', '', '', ''],
        ['', '', '', '', 'Total', '','', '', '', '', ''],
        [
          '', '', '', '', '', `Comisión >>>>>`,'',
          formatNumber(primaNetaTotal),
          formatNumber(participacionTotal),
          '',
          formatNumber(comisionPrimaNetaTotal),
        ]
      );
    }

    doc.autoTable({
      startY: currentY + 20,
      body,
      margin: { left: 10 },
      styles: { fontSize: 9, rowHeight: 5 },
      headStyles: { fontSize: 10 },
      columnStyles: {
        0: { halign: 'left' },
        1: { halign: 'left' },
        2: { halign: 'right' },
        3: { halign: 'left' },
        4: { halign: 'right' },
        5: { halign: 'left' },
        6: { halign: 'right' },
        7: { halign: 'right' },
        8: { halign: 'right' },
        9: { halign: 'right' },
      },
      didParseCell: (row: any) => {
        const isBody = row.section === 'body';
        const lastIndex = row.table.body.length - 1;
        const currentIndex = row.row.index;

        if (currentIndex === 0 && isBody) {
          Object.assign(row.cell.styles, {
            valign: 'bottom',
            lineWidth: { bottom: 1 },
            lineColor: [255, 165, 0],
            fillColor: [255, 255, 255],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            fontSize: 10
          });
        }

        // Últimas filas con estilos especiales
        if (idx === subramos.length - 1) {
          const estiloComun = {
            fillColor: [255, 255, 255],
            textColor: [0, 0, 0],
            fontStyle: 'bold'
          };
          if (currentIndex === lastIndex - 1 && isBody) Object.assign(row.cell.styles, { ...estiloComun, fontSize: 10 });
          if (currentIndex === lastIndex && isBody) Object.assign(row.cell.styles, {
            ...estiloComun,
            lineWidth: { bottom: 1 },
            lineColor: [52, 201, 235]
          });
        } else if (currentIndex === lastIndex && isBody) {
          Object.assign(row.cell.styles, {
            fillColor: [255, 255, 255],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            fontSize: 10
          });
        }
      }
    });

    currentY = doc.lastAutoTable.finalY - 10;
  });

  doc.save("Comisiones Detalles por Cobro.pdf");
};
